import { Box, Button, Typography } from '@mui/material';

import {
  updateSelectedPlan,
  usePersistentplanAndFreq,
} from 'store/planAndFreq';
import { usePersistentPromoCodeDetails } from 'store/promoCode';
import theme from 'src/style-system/theme';
import { plansType } from '@utils/types';
import { getDecimalPricePerMeal, getPriceAfterDiscount } from '@utils/helpers';
import { getBorderRadius } from '@components/FrequencyButtonGroup';

import { useStyles } from './useStyles';
import { useMemo } from 'react';

interface IPlanAndPriceButtonGroup {
  plans: plansType[] | undefined;
}

const PlanAndPriceButtonGroup = ({ plans }: IPlanAndPriceButtonGroup) => {
  const planAndFreq = usePersistentplanAndFreq();

  const classes = useStyles();

  const promoCodeDetails = usePersistentPromoCodeDetails();

  const { isPromoValid, amountType, amount } = promoCodeDetails || {};

  const updatePlan = (plan: number, planId: number) => {
    updateSelectedPlan(plan, planId);
  };

  const getFreeMealsCount = (countOfDishes: number) => {
    if (countOfDishes === 20) {
      return 4;
    } else if (countOfDishes === 10) {
      return 2;
    } else if (countOfDishes === 5) {
      return 1;
    }
  };

  return (
    <>
      <Box className={classes.desktopPlanBtnWrapper}>
        {plans &&
          plans[0]
            ?.filter((plan) => plan.count_of_dishes !== 15)
            ?.map((plan) => (
              <Button
                key={plan.id}
                variant="contained"
                color={
                  planAndFreq.selectedPlan === plan.count_of_dishes
                    ? 'turmeric'
                    : 'offWhite'
                }
                sx={{
                  textTransform: 'none',
                  padding: '20px 10px',
                  width: { xs: '185px', lg: '225px' },
                  height: '105px',
                  display: 'flex',
                  alignItems: 'flex-end',
                  columnGap: '10px',
                  borderRadius: '15px',
                  border: `2px solid ${
                    planAndFreq.selectedPlanId === plan.id
                      ? theme.palette.turmeric.main
                      : theme.palette.cinnamon.main
                  }`,
                }}
                onClick={() => updatePlan(plan.count_of_dishes, plan.id)}
              >
                <Box>
                  <Typography
                    variant="h3"
                    color="garamMasala.main"
                    align="center"
                  >
                    {plan.count_of_dishes}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="garamMasala.main"
                    align="center"
                    width={{ xs: 50, md: 64 }}
                  >
                    Meals
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="garamMasala.main"
                    align="center"
                  >
                    {plan.currency_symbol +
                      getPriceAfterDiscount({
                        price: plan.price,
                        amount:
                          !isPromoValid && amountType
                            ? 0
                            : isPromoValid
                            ? amount
                            : '15',
                        amountType: amountType,
                      }).toFixed(2) +
                      '/meal'}
                  </Typography>
                  {(isPromoValid && amountType === 'ABSOLUTE') ||
                  (!isPromoValid && amountType) ||
                  (isPromoValid &&
                    (amountType === 'FREE_MEALS' ||
                      amountType === 'FREE_GIFTS')) ? null : (
                    <Typography
                      variant="caption"
                      color="cinnamon.main"
                      align="center"
                      sx={{
                        textDecoration: 'line-through',
                      }}
                    >
                      {plan.currency_symbol +
                        getDecimalPricePerMeal(plan.price).toFixed(2) +
                        '/meal'}
                    </Typography>
                  )}
                </Box>
              </Button>
            ))}
      </Box>
      <Box>
        <Box className={classes.mobilePlanBtnWrapper}>
          {plans &&
            plans[0]
              ?.filter((plan) => plan.count_of_dishes !== 15)
              ?.map((plan, index) => (
                <Button
                  variant="contained"
                  color={
                    planAndFreq.selectedPlan === plan.count_of_dishes
                      ? 'turmeric'
                      : index % 2
                      ? 'whitePepper'
                      : 'offWhite'
                  }
                  key={plan.id}
                  sx={{
                    textTransform: 'none',
                    width: '100px',
                    padding: '8px 12px',
                    borderRadius: getBorderRadius(
                      index,
                      plans[0]?.filter((plan) => plan.count_of_dishes !== 15)
                        ?.length
                    ),
                    zIndex:
                      planAndFreq.selectedPlan === plan.count_of_dishes ? 1 : 0,
                    transform:
                      planAndFreq.selectedPlan === plan.count_of_dishes
                        ? 'scale(1.15)'
                        : '',
                    '&:hover': {
                      backgroundColor:
                        planAndFreq.selectedPlan === plan.count_of_dishes
                          ? 'turmeric.main'
                          : index !== 1
                          ? 'offWhite.main'
                          : 'whitePepper.main',
                    },
                  }}
                  onClick={() => updatePlan(plan.count_of_dishes, plan.id)}
                >
                  <Box className={classes.planAmountWrapper}>
                    <Typography
                      variant="body2"
                      color="garamMasala.main"
                      align="center"
                    >
                      {plan.count_of_dishes} Meals
                    </Typography>
                    <Box key={plan.id} className={classes.planAmount}>
                      <Typography
                        variant="caption"
                        color="garamMasala.main"
                        align="center"
                      >
                        {plan.currency_symbol +
                          getPriceAfterDiscount({
                            price: plan.price,
                            amount:
                              !isPromoValid && amountType
                                ? 0
                                : isPromoValid
                                ? amount
                                : '15',
                            amountType: amountType,
                          }).toFixed(2) +
                          '/meal'}
                      </Typography>

                      {(isPromoValid && amountType === 'ABSOLUTE') ||
                      (!isPromoValid && amountType) ||
                      (isPromoValid &&
                        (amountType === 'FREE_MEALS' ||
                          amountType === 'FREE_GIFTS')) ? null : (
                        <Typography
                          variant="caption"
                          color="cinnamon.main"
                          align="center"
                          sx={{
                            textDecoration: 'line-through',
                          }}
                        >
                          {plan.currency_symbol +
                            getDecimalPricePerMeal(plan.price) +
                            '/meal'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Button>
              ))}
        </Box>
        {isPromoValid && amountType === 'FREE_MEALS' ? (
          <Box className={classes.freeMealCountWrapper}>
            {plans &&
              plans[0]
                ?.filter((plan) => plan.count_of_dishes !== 15)
                ?.map((plan, index) => (
                  <Typography
                    variant="body2"
                    color="cinnamon.main"
                    align="center"
                  >
                    {`${getFreeMealsCount(plan.count_of_dishes)} free ${
                      plan.count_of_dishes === 5 ? 'meal' : 'meals'
                    }`}
                  </Typography>
                ))}
          </Box>
        ) : null}
      </Box>
    </>
  );
};

export default PlanAndPriceButtonGroup;
